import React, { useState, useEffect, useMemo } from 'react';
import { trackClick } from '../utils/yandexMetrika';

interface NavItem {
    label: string;
    href: string;
}

interface NavigationProps {
    showPortfolio?: boolean;
    onBack?: () => void;
}

const Navigation: React.FC<NavigationProps> = ({ showPortfolio = false, onBack }) => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [activeSection, setActiveSection] = useState('');
    const [showToTop, setShowToTop] = useState(false);
    const [isAtTop, setIsAtTop] = useState(true);
    const [isInHero, setIsInHero] = useState(true);

    const navItems: NavItem[] = useMemo(() => [
        { label: 'О себе', href: '#about' },
        { label: 'Услуги', href: '#info' },
        { label: 'Форматы', href: '#formats' },
        { label: 'Партнёры', href: '#partners' },
        { label: 'Контакты', href: '#contact' },
        { label: 'Портфолио', href: '#portfolio' },
    ], []);

    useEffect(() => {
        const handleScroll = () => {
            const scrollY = window.scrollY;
            setIsScrolled(scrollY > 50);
            setShowToTop(scrollY > 300); // Show "Наверх" when scrolled more than 300px
            setIsAtTop(scrollY < 100);

            // Check if we're in hero section
            const heroSection = document.getElementById('home');
            if (heroSection) {
                const heroBottom = heroSection.offsetTop + heroSection.offsetHeight;
                setIsInHero(scrollY < heroBottom - 100);
            }

            // Determine active section based on scroll position
            const sections = navItems.map(item => item.href.substring(1));
            const scrollPosition = scrollY + 200;

            for (let i = sections.length - 1; i >= 0; i--) {
                const section = document.getElementById(sections[i]);
                if (section && section.offsetTop <= scrollPosition) {
                    setActiveSection(sections[i]);
                    break;
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Initial check
        return () => window.removeEventListener('scroll', handleScroll);
    }, [navItems]);

    const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
        e.preventDefault();
        const targetId = href.substring(1);
        
        // Отслеживание клика по навигации
        trackClick(`nav_${targetId || 'home'}`);

        // Если это портфолио, вызываем событие для переключения страницы
        if (targetId === 'portfolio') {
            window.dispatchEvent(new CustomEvent('openPortfolio'));
            return;
        }

        const targetElement = document.getElementById(targetId);
        if (targetElement) {
            const offsetTop = targetElement.offsetTop - 80; // Account for fixed header
            window.scrollTo({
                top: offsetTop,
                behavior: 'smooth'
            });
        }
    };

    return (
        <nav className={`navigation ${isScrolled ? 'scrolled' : ''} ${isInHero ? 'in-hero' : ''}`}>
            <div className="nav-container">
                {showPortfolio && onBack ? (
                    <div className="nav-portfolio-header">
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                trackClick('portfolio_back');
                                onBack();
                            }}
                            className="nav-back-icon"
                            title="Назад"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M19 12H5M5 12l6-6M5 12l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </a>
                        <h1 className="nav-portfolio-title"><strong>Портфолио</strong></h1>
                    </div>
                ) : (
                    <ul className="nav-menu">
                        {showToTop && (
                            <li>
                                <a
                                    href="#home"
                                    onClick={(e) => handleNavClick(e, '#home')}
                                    className={isAtTop ? 'active' : ''}
                                    title="Наверх"
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M5 12l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </a>
                            </li>
                        )}
                        {navItems.map((item) => (
                            <li key={item.href} className={item.href === '#portfolio' ? 'nav-portfolio-item' : ''}>
                                <a
                                    href={item.href}
                                    onClick={(e) => handleNavClick(e, item.href)}
                                    className={activeSection === item.href.substring(1) ? 'active' : ''}
                                >
                                    <strong>{item.label}</strong>
                                </a>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </nav>
    );
};

export default Navigation;
